import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, Database, ChevronRight, Play, X } from 'lucide-react';
import { dataLakeMonitorApi } from '../../../lib/apiClient';
import type {
  DataLakeStatusResponse,
  SessionSnapshotItem,
  DatasetSnapshotItem,
  DatasetRowsResponse,
  DataLakeQueryResponse,
} from '../../../types/types';
import { toast } from '../../../lib/toast';
import DataAnalystExamples from './DataAnalystExamples';
import styles from './DataLakeLiveView.module.css';

type View =
  | { level: 'sessions' }
  | { level: 'session'; sessionId: string }
  | { level: 'rows'; sessionId: string; dataset: DatasetSnapshotItem };

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
}

function expiresIn(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 'expired';
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m`;
}

const DataLakeLiveView: React.FC = () => {
  const [status, setStatus] = useState<DataLakeStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>({ level: 'sessions' });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [dropping, setDropping] = useState<string | null>(null);

  const [rowsData, setRowsData] = useState<DatasetRowsResponse | null>(null);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState<string | null>(null);
  const [rowsPage, setRowsPage] = useState(1);
  const [query, setQuery] = useState('');
  const [queryResult, setQueryResult] = useState<DataLakeQueryResponse | null>(null);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [queryLoading, setQueryLoading] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setError(null);
      const data = await dataLakeMonitorApi.getStatus();
      setStatus(data);
    } catch {
      setError('Failed to load data lake status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchStatus, 10000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchStatus]);

  const fetchRows = useCallback(async (sessionId: string, datasetId: string, page: number) => {
    setRowsLoading(true);
    setRowsError(null);
    try {
      const data = await dataLakeMonitorApi.getDatasetRows(sessionId, datasetId, page, 50);
      setRowsData(data);
    } catch {
      setRowsError('Failed to load rows');
    } finally {
      setRowsLoading(false);
    }
  }, []);

  const handleBrowseRows = (sessionId: string, dataset: DatasetSnapshotItem) => {
    setView({ level: 'rows', sessionId, dataset });
    setRowsPage(1);
    setRowsData(null);
    setQuery(`SELECT *\nFROM "${dataset.datasetId}"\nLIMIT 50`);
    setQueryResult(null);
    setQueryError(null);
    fetchRows(sessionId, dataset.datasetId, 1);
  };

  const handleRunQuery = async () => {
    if (view.level !== 'rows' || !query.trim()) return;
    setQueryLoading(true);
    setQueryError(null);
    try {
      const result = await dataLakeMonitorApi.runQuery(view.sessionId, view.dataset.datasetId, query);
      setQueryResult(result);
    } catch (err) {
      setQueryResult(null);
      setQueryError(err instanceof Error ? err.message : 'Failed to run query');
    } finally {
      setQueryLoading(false);
    }
  };

  const handleClearQuery = () => {
    setQuery('');
    setQueryResult(null);
    setQueryError(null);
  };

  const handlePageChange = (newPage: number) => {
    if (view.level !== 'rows') return;
    setRowsPage(newPage);
    fetchRows(view.sessionId, view.dataset.datasetId, newPage);
  };

  const handleDrop = async (sessionId: string) => {
    setDropping(sessionId);
    try {
      await dataLakeMonitorApi.dropSession(sessionId);
      toast.success('Session dropped');
      if (view.level !== 'sessions' && ('sessionId' in view) && view.sessionId === sessionId) {
        setView({ level: 'sessions' });
      }
      await fetchStatus();
    } catch {
      toast.error('Failed to drop session');
    } finally {
      setDropping(null);
    }
  };

  const getSessionFromStatus = (sessionId: string): SessionSnapshotItem | undefined =>
    status?.sessions.find((s) => s.sessionId === sessionId);

  const totalDatasets = status?.sessions.reduce((sum, s) => sum + s.datasets.length, 0) ?? 0;

  // --- RENDER LEVELS ---

  const renderToolbar = () => (
    <div className={styles.toolbar}>
      <button
        className={styles.refreshBtn}
        onClick={() => { setLoading(true); fetchStatus(); }}
        disabled={loading}
      >
        <RefreshCw size={13} />
        Refresh
      </button>
      <label className={styles.autoRefreshLabel}>
        <input
          type="checkbox"
          checked={autoRefresh}
          onChange={(e) => setAutoRefresh(e.target.checked)}
        />
        Auto-refresh (10s)
      </label>
    </div>
  );

  const renderSummary = () => (
    <div className={styles.summaryRow}>
      <span className={styles.chip}>
        <Database size={12} />
        <span className={styles.chipValue}>{status?.sessions.length ?? 0}</span> active sessions
      </span>
      <span className={styles.chip}>
        <span className={styles.chipValue}>{totalDatasets}</span> datasets
      </span>
      <span className={styles.chip}>
        <span className={styles.chipValue}>{formatBytes(status?.totalMemoryBytes ?? 0)}</span> in use
      </span>
    </div>
  );

  const renderBreadcrumb = () => {
    if (view.level === 'sessions') return null;

    const sessionId = 'sessionId' in view ? view.sessionId : '';
    const shortId = sessionId.length > 16 ? `${sessionId.slice(0, 16)}…` : sessionId;

    return (
      <div className={styles.breadcrumb}>
        <button className={styles.breadcrumbLink} onClick={() => setView({ level: 'sessions' })}>
          Live View
        </button>
        <ChevronRight size={12} className={styles.breadcrumbSep} />
        {view.level === 'session' && (
          <span className={styles.breadcrumbCurrent}>{shortId}</span>
        )}
        {view.level === 'rows' && (
          <>
            <button
              className={styles.breadcrumbLink}
              onClick={() => setView({ level: 'session', sessionId: view.sessionId })}
            >
              <span className={styles.breadcrumbCurrent}>{shortId}</span>
            </button>
            <ChevronRight size={12} className={styles.breadcrumbSep} />
            <span className={styles.breadcrumbCurrent}>{view.dataset.toolName} / {view.dataset.arrayKey}</span>
          </>
        )}
      </div>
    );
  };

  const renderSessionsTable = () => {
    if (!status || status.sessions.length === 0) {
      return (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>⚡</div>
          No active sessions. Data lake sessions are created when tools are invoked during a chat.
        </div>
      );
    }

    return (
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Session ID</th>
            <th>Last Activity</th>
            <th>Datasets</th>
            <th>Memory</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {status.sessions.map((s) => (
            <tr
              key={s.sessionId}
              className={styles.tableRow}
              onClick={() => setView({ level: 'session', sessionId: s.sessionId })}
            >
              <td>
                <span className={styles.sessionIdCell} title={s.sessionId}>
                  {s.sessionId.length > 20 ? `${s.sessionId.slice(0, 20)}…` : s.sessionId}
                </span>
              </td>
              <td><span className={styles.muted}>{relativeTime(s.lastActivityAt)}</span></td>
              <td>{s.datasets.length}</td>
              <td>{formatBytes(s.memoryBytes)}</td>
              <td onClick={(e) => e.stopPropagation()}>
                <button
                  className={styles.dropBtn}
                  disabled={dropping === s.sessionId}
                  onClick={() => handleDrop(s.sessionId)}
                >
                  {dropping === s.sessionId ? 'Dropping…' : 'Drop'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderSessionDetail = (sessionId: string) => {
    const session = getSessionFromStatus(sessionId);
    if (!session) {
      return (
        <div className={styles.empty}>
          Session not found - it may have expired. <button className={styles.breadcrumbLink} onClick={() => { fetchStatus(); setView({ level: 'sessions' }); }}>Refresh</button>
        </div>
      );
    }

    return (
      <>
        <div className={styles.sessionDetailHeader}>
          <div className={styles.summaryRow}>
            <span className={styles.chip}><span className={styles.chipValue}>{session.datasets.length}</span> datasets</span>
            <span className={styles.chip}><span className={styles.chipValue}>{formatBytes(session.memoryBytes)}</span> used</span>
            <span className={styles.chip}>last active <span className={styles.chipValue}>{relativeTime(session.lastActivityAt)}</span></span>
          </div>
          <button
            className={styles.dropBtn}
            disabled={dropping === sessionId}
            onClick={() => handleDrop(sessionId)}
          >
            {dropping === sessionId ? 'Dropping…' : 'Drop Session'}
          </button>
        </div>

        {session.datasets.length === 0 ? (
          <div className={styles.empty}>No datasets in this session.</div>
        ) : (
          <div className={styles.datasetGrid}>
            {session.datasets.map((d) => (
              <div key={d.datasetId} className={styles.datasetCard}>
                <div className={styles.datasetCardHeader}>
                  {d.derived ? (
                    <div>
                      <div className={styles.toolName}>
                        <span className={styles.derivedBadge}>Derived</span> {d.derived.name}
                      </div>
                      <div className={styles.datasetId}>{d.datasetId}</div>
                      {d.derived.description && (
                        <div className={styles.paramsSummary}>{d.derived.description}</div>
                      )}
                      <div className={styles.paramsSummary}>
                        Built from: {d.derived.sourceDatasetIds.join(', ') || '-'}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className={styles.toolName}>{d.toolName}</div>
                      <div className={styles.datasetId}>
                        table: <strong>{d.arrayKey}</strong> &nbsp;·&nbsp; {d.datasetId}
                      </div>
                      {d.paramsSummary && (
                        <div className={styles.paramsSummary}>{d.paramsSummary}</div>
                      )}
                    </div>
                  )}
                </div>

                <div className={styles.datasetMeta}>
                  <span className={styles.datasetMetaItem}>
                    <span className={styles.datasetMetaLabel}>Rows:</span> {d.rowCount.toLocaleString()}
                  </span>
                  <span className={styles.datasetMetaItem}>
                    <span className={styles.datasetMetaLabel}>Cols:</span> {d.columnCount}
                  </span>
                  <span className={styles.datasetMetaItem}>
                    <span className={styles.datasetMetaLabel}>Memory:</span> {formatBytes(d.memoryBytes)}
                  </span>
                  <span className={styles.datasetMetaItem}>
                    <span className={styles.datasetMetaLabel}>Expires:</span> {expiresIn(d.expiresAt)}
                  </span>
                </div>

                <div className={styles.schemaChips}>
                  {d.schema.map((col) => (
                    <span key={col.name} className={styles.schemaChip}>
                      {col.name} <span className={styles.schemaChipType}>{col.type}</span>
                    </span>
                  ))}
                </div>

                {d.derived && (
                  <details className={styles.sqlDetails}>
                    <summary>SQL</summary>
                    <pre className={styles.sqlBlock}>{d.derived.sql}</pre>
                  </details>
                )}

                <button className={styles.browseBtn} onClick={() => handleBrowseRows(sessionId, d)}>
                  Browse rows →
                </button>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  const renderRowsView = (sessionId: string, dataset: DatasetSnapshotItem) => {
    const startRow = rowsData ? (rowsData.page - 1) * rowsData.pageSize + 1 : 0;
    const endRow = rowsData ? Math.min(rowsData.page * rowsData.pageSize, rowsData.totalRows) : 0;
    const visibleColumns = queryResult?.columns ?? dataset.schema;
    const visibleRows = queryResult?.rows ?? rowsData?.rows ?? [];

    return (
      <>
        <div className={styles.rowsHeader}>
          <div className={styles.rowsMeta}>
            {queryResult
              ? `Query returned ${queryResult.row_count.toLocaleString()} rows in ${queryResult.duration_ms} ms`
              : rowsData
              ? `Rows ${startRow.toLocaleString()}–${endRow.toLocaleString()} of ${rowsData.totalRows.toLocaleString()}`
              : `${dataset.rowCount.toLocaleString()} total rows`}
          </div>
        </div>

        {rowsLoading && <div className={styles.loading}>Loading rows…</div>}

        {rowsError && (
          <div className={styles.errorBanner}>
            {rowsError}
            <button className={styles.refreshBtn} onClick={() => fetchRows(sessionId, dataset.datasetId, rowsPage)}>
              Retry
            </button>
          </div>
        )}

        {!rowsLoading && (rowsData || queryResult) && (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    {visibleColumns.map((col) => (
                      <th key={col.name} title={col.type}>{col.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row, i) => (
                    <tr key={i}>
                      {visibleColumns.map((col) => {
                        const val = row[col.name];
                        return (
                          <td key={col.name} title={val === null || val === undefined ? 'null' : String(val)}>
                            {val === null || val === undefined
                              ? <span className={styles.nullCell}>null</span>
                              : String(val)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {visibleRows.length === 0 && (
                <div className={styles.emptyTable}>The query returned no rows.</div>
              )}
            </div>

            {!queryResult && rowsData && (
              <div className={styles.pagination}>
                <span>Page {rowsData.page} of {rowsData.totalPages}</span>
                <button
                  className={styles.pageBtn}
                  disabled={rowsData.page <= 1 || rowsLoading}
                  onClick={() => handlePageChange(rowsData.page - 1)}
                >
                  ← Prev
                </button>
                <button
                  className={styles.pageBtn}
                  disabled={rowsData.page >= rowsData.totalPages || rowsLoading}
                  onClick={() => handlePageChange(rowsData.page + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </>
    );
  };

  const renderQueryPanel = () => {
    if (view.level !== 'rows') return null;

    return (
      <section className={styles.queryPanel} aria-label="SQL query editor">
        <div className={styles.queryHeader}>
          <div>
            <div className={styles.queryTitle}>Run a SELECT query</div>
            <div className={styles.queryHint}>DuckDB SQL · maximum 200 result rows</div>
          </div>
          <div className={styles.queryActions}>
            {(query || queryResult) && (
              <button className={styles.clearQueryBtn} onClick={handleClearQuery} disabled={queryLoading}>
                <X size={13} />
                Clear query
              </button>
            )}
            <button
              className={styles.runQueryBtn}
              onClick={handleRunQuery}
              disabled={queryLoading || !query.trim()}
            >
              <Play size={13} fill="currentColor" />
              {queryLoading ? 'Running…' : 'Run'}
            </button>
          </div>
        </div>
        <textarea
          className={styles.queryEditor}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
              event.preventDefault();
              handleRunQuery();
            }
          }}
          spellCheck={false}
          aria-label="SELECT query"
        />
        {queryError && <div className={styles.queryError}>{queryError}</div>}
      </section>
    );
  };

  if (loading && !status) return <div className={styles.loading}>Loading…</div>;

  return (
    <div className={styles.root}>
      {error && (
        <div className={styles.errorBanner}>
          {error}
          <button className={styles.refreshBtn} onClick={() => { setLoading(true); fetchStatus(); }}>
            Retry
          </button>
        </div>
      )}

      <div className={view.level === 'rows' ? styles.topGrid : undefined}>
        <div>
          {renderToolbar()}
          {renderSummary()}
          {renderBreadcrumb()}
        </div>
        {renderQueryPanel()}
      </div>

      {view.level === 'sessions' && renderSessionsTable()}
      {view.level === 'sessions' && <DataAnalystExamples />}
      {view.level === 'session' && renderSessionDetail(view.sessionId)}
      {view.level === 'rows' && renderRowsView(view.sessionId, view.dataset)}
    </div>
  );
};

export default DataLakeLiveView;
